// Singleton to store the current playing Howl instance
class CurrentAudio {
  constructor() {
    this.current = null;
  }
  set(howl) {
    this.current = howl;
  }
  get() {
    return this.current;
  }
  clear() {
    this.current = null;
  }
}

export const currentAudioSingleton = new CurrentAudio();

// Prevents any Howl from actually playing while an overlay (e.g. the lite session play button) is up
let overlayBlocked = false;
export const setOverlayBlocked = blocked => {
  overlayBlocked = blocked;
};

import { Howl } from 'howler';
import HowlerWithCCs from './captionsHelper';

const ERR_HOWL = src => ({
  /* eslint-disable no-console */
  play: () => console.warn(`There was an error loading the sound ${src}, unable to play it.`),
  stop: () => console.warn(`There was an error loading the sound ${src}, unable to stop it.`)
  /* eslint-enable no-console */
});

const _isValidAudioSrc = src => src.endsWith('.mp3') || src.includes('.mp3?');

export default class AssetLoader {
  constructor() {
    this.cache = new Map();
    this.queue = {
      images: [],
      sounds: []
    };
  }
  _notInCache = filename => !this.cache.has(filename);

  _addToQueue = (asset, queueName) => {
    if (!asset || !queueName) {
      return;
    }

    this.queue[queueName] = [...new Set([...this.queue[queueName], asset])];
  };

  _addToCache = (src, asset) => this.cache.set(src, asset);

  _loadImage = imgSrc =>
    new Promise(resolve => {
      const image = new Image();
      const src = typeof imgSrc === 'string' ? imgSrc : imgSrc.src;
      image.onload = () => resolve(image);
      image.onerror = () => resolve(image);
      image.src = src;
    });

  _loadAudio = options => async audioSrc =>
    new Promise(resolve => {
      const { src, captions } =
        typeof audioSrc === 'object' ? audioSrc : { src: audioSrc, captions: null };
      if (typeof src !== 'string' || !_isValidAudioSrc(src))
        return resolve(this._addToCache(src, ERR_HOWL(src)));

      const audio = new Howl({
        src,
        ...options,
        onplay: () => {
          if (overlayBlocked) {
            audio.pause();
            return;
          }
          // Set the current playing audio instance
          currentAudioSingleton.set(audio);
        },

        onend: () => {
          // Optionally clear when finished
          currentAudioSingleton.clear();
        },
        onstop: () => {
          currentAudioSingleton.clear();
        },
        onload: () => resolve(this._addToCache(src, HowlerWithCCs(audio, captions))),
        onloaderror: (e, msg) => {
          console.log(msg); //eslint-disable-line no-console
          resolve(ERR_HOWL(src));
          resolve(this._addToCache(src, ERR_HOWL(src)));
        }
      });
    });

  _downloadAssets = async (queue, loader) => {
    await Promise.all(queue.map(loader));

    // reset queue
    this.queue = {
      images: [],
      sounds: []
    };

    return this.cache;
  };

  _downloadImages = () => this._downloadAssets(this.queue.images, this._loadImage);

  _downloadAudio = () => {
    const audioLoader = this._loadAudio({
      preload: true,
      html5: false
    });

    return this._downloadAssets(this.queue.sounds, audioLoader);
  };

  queueImage = imageAssetPath => this._addToQueue(imageAssetPath, 'images');

  queueAudio = audioAssetPath => this._addToQueue(audioAssetPath, 'sounds');

  async downloadAll() {
    await Promise.all([
      this._downloadImages(this.queue.images),
      this._downloadAudio(this.queue.sounds)
    ]);
    return Promise.resolve(this.cache);
  }
}
